/**
 * Settings page with tabbed interface.
 *
 * The General tab displays user profile fields (username, plan tier),
 * scanner engine LLM configuration (provider, API key, model),
 * and account actions (change password, delete account).
 *
 * The Email Addresses tab lets the user manage their email addresses.
 * The API Keys tab manages scanner API keys.
 * The Advanced tab exposes client-side preferences such as the visual theme.
 *
 * @module pages/settings
 */

import React, { useState,
                useEffect,
                useCallback,
                useRef }        from 'react';
import { useSearchParams }      from 'react-router-dom';
import { X }                    from 'lucide-react';
import Layout                   from '../components/Layout';
import EmailAddressesTab        from '../components/EmailAddressesTab';
import ApiKeysTab               from '../components/ApiKeysTab';
import AdvancedTab              from '../components/AdvancedTab';
import ChangePasswordModal      from '../components/ChangePasswordModal';
import DeleteAccountModal       from '../components/DeleteAccountModal';
import { useI18n }              from '../dataProviders/I18nProvider';
import { useAuth }              from '../hooks/useAuth';
import apiClient                from '../procedural/apiClient';

/** Tab identifiers. */
const TABS = ['general', 'emailAddresses', 'apiKeys', 'advanced'];

/**
 * Renders the Settings page.
 *
 * @returns {React.ReactElement}
 */
export default function Settings() {
  const { t }                         = useI18n();
  const { user }                      = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam  = searchParams.get('tab');
  const activeTab = TABS.includes(tabParam) ? tabParam : 'general';

  function handleTabChange(tab) {
    setSearchParams({ tab }, { replace: true });
  }

  return (
    <Layout title={t('page.settings')}>
      {/* Tab bar */}
      <div className="settings-tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`settings-tab${activeTab === tab ? ' settings-tab--active' : ''}`}
            onClick={() => handleTabChange(tab)}
          >
            {t(`settings.tab.${tab}`)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'general'        && <GeneralTab user={user} />}
      {activeTab === 'emailAddresses' && <EmailAddressesTab />}
      {activeTab === 'apiKeys'        && <ApiKeysTab />}
      {activeTab === 'advanced'       && <AdvancedTab />}
    </Layout>
  );
}

/* ----------------------------------------------------------------------- */
/*  General Tab                                                            */
/* ----------------------------------------------------------------------- */

/**
 * Renders the General settings tab with two panels:
 * - Left: form fields (username, plan tier, LLM, API key, model)
 * - Right: action buttons (change password, delete account)
 *
 * @param {object} props
 * @param {object} props.user - Current user object from AuthProvider.
 * @returns {React.ReactElement}
 */
function GeneralTab({ user }) {
  const { t } = useI18n();

  /* ---- Server-loaded state ---------------------------------------------- */

  const [availableLlms, setAvailableLlms] = useState([]);
  const [serverLlmId, setServerLlmId]     = useState(user?.scannerEngineLlmId || '');
  const [serverModel, setServerModel]     = useState(user?.scannerEngineLlmModel || '');
  const [serverMaskedKey, setServerMaskedKey] = useState('');

  /* ---- Form state ------------------------------------------------------- */

  const [llmId, setLlmId]         = useState(user?.scannerEngineLlmId || '');
  const [apiKey, setApiKey]       = useState('');
  const [model, setModel]         = useState(user?.scannerEngineLlmModel || '');
  const [apiKeyTouched, setApiKeyTouched] = useState(false);

  /* ---- Model listing state ---------------------------------------------- */

  const [models, setModels]               = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError]     = useState(null);

  /* ---- UI state --------------------------------------------------------- */

  const [saving, setSaving]                             = useState(false);
  const [saveSuccess, setSaveSuccess]                   = useState(null);
  const [saveError, setSaveError]                       = useState(null);
  const [showPasswordModal, setShowPasswordModal]       = useState(false);
  const [showDeleteModal, setShowDeleteModal]           = useState(false);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(null);

  const modelFetchRef  = useRef(0);
  const initialStateRef = useRef(null);

  /* ---- Resolve LLM name from id ---------------------------------------- */

  /**
   * @param {string} id - LLM UUID.
   * @returns {string} LLM name or empty string.
   */
  function llmNameById(id) {
    const llm = availableLlms.find((l) => l.id === id);
    return llm ? llm.name : '';
  }

  /* ---- Fetch models when LLM + API key are available -------------------- */

  const fetchModels = useCallback(async (llmName, key, saveSnapshot = false) => {
    const fetchId = ++modelFetchRef.current;
    setModelsLoading(true);
    setModelsError(null);
    setModels([]);

    try {
      const res = await apiClient.post('/api/settings/llm-models', {
        llmName,
        apiKey: key,
      });

      if (fetchId !== modelFetchRef.current) return;

      const fetchedModels = res.data.models || [];
      setModels(fetchedModels);

      if (saveSnapshot) {
        initialStateRef.current = { models: fetchedModels };
      }
    } catch {
      if (fetchId !== modelFetchRef.current) return;
      setModelsError(t('settings.model.error'));
    } finally {
      if (fetchId === modelFetchRef.current) {
        setModelsLoading(false);
      }
    }
  }, [t]);

  /* ---- Load settings from server ---------------------------------------- */

  useEffect(() => {
    apiClient.get('/api/settings')
      .then((res) => {
        const { user: u, availableLlms: llms } = res.data;
        setAvailableLlms(llms);
        setServerLlmId(u.scannerEngineLlmId || '');
        setServerModel(u.scannerEngineLlmModel || '');
        setServerMaskedKey(u.maskedApiKey || '');
        setLlmId(u.scannerEngineLlmId || '');
        setModel(u.scannerEngineLlmModel || '');

        if (u.scannerEngineLlmId && u.maskedApiKey) {
          const llm = llms.find((l) => l.id === u.scannerEngineLlmId);
          if (llm) {
            fetchModels(llm.name, '', true);
          }
        }
      })
      .catch(() => {
        // Settings were already loaded by AuthProvider; use what we have.
      });
  }, [fetchModels]);

  /* ---- Handlers --------------------------------------------------------- */

  /**
   * Handle LLM selection change.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} e
   */
  function handleLlmChange(e) {
    const newId = e.target.value;
    setLlmId(newId);
    setApiKey('');
    setApiKeyTouched(false);
    setModel('');
    setModels([]);
    setModelsError(null);
  }

  /**
   * Handle API key input change.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  function handleApiKeyChange(e) {
    const key = e.target.value;
    setApiKey(key);
    setApiKeyTouched(true);
    setModel('');
    setModels([]);
    setModelsError(null);
  }

  /**
   * Handle API key blur — trigger model fetch if both LLM and key are set.
   */
  function handleApiKeyBlur() {
    if (llmId && apiKey) {
      fetchModels(llmNameById(llmId), apiKey);
    }
  }

  /**
   * Handle API key clear button — marks intent to delete the vault key.
   */
  function handleApiKeyClear() {
    setApiKey('');
    setApiKeyTouched(true);
    setModel('');
    setModels([]);
    setModelsError(null);
  }

  /**
   * Handle model selection or manual entry change.
   *
   * @param {React.ChangeEvent<HTMLSelectElement|HTMLInputElement>} e
   */
  function handleModelChange(e) {
    setModel(e.target.value);
  }

  /* ---- Dirty detection -------------------------------------------------- */

  const llmChanged   = llmId !== serverLlmId;
  const modelChanged = model !== serverModel;
  const keyEntered   = apiKeyTouched && apiKey !== '';
  const keyCleared   = apiKeyTouched && apiKey === '' && serverMaskedKey !== '';
  const keyChanged   = keyEntered || keyCleared;
  const isDirty      = llmChanged || modelChanged || keyChanged;

  /* ---- Save ------------------------------------------------------------- */

  /**
   * Save settings to the server.
   */
  async function handleSave() {
    setSaving(true);
    setSaveSuccess(null);
    setSaveError(null);

    try {
      const payload = {
        scannerEngineLlmId:    llmId || null,
        scannerEngineLlmModel: model || null,
      };

      if (apiKeyTouched) {
        payload.scannerEngineLlmApiKey = apiKey;
      }

      await apiClient.put('/api/settings', payload);

      setServerLlmId(llmId);
      setServerModel(model);
      if (apiKeyTouched && apiKey) {
        setServerMaskedKey('\u2022'.repeat(8) + apiKey.slice(-4));
      } else if (apiKeyTouched && !apiKey) {
        setServerMaskedKey('');
      }
      setApiKey('');
      setApiKeyTouched(false);
      setSaveSuccess(t('settings.saved'));

      initialStateRef.current = { models };
    } catch {
      setSaveError(t('settings.error.generic'));
    } finally {
      setSaving(false);
    }
  }

  /* ---- Revert ----------------------------------------------------------- */

  /**
   * Reset form to last-saved server state.
   */
  function handleRevert() {
    setLlmId(serverLlmId);
    setModel(serverModel);
    setApiKey('');
    setApiKeyTouched(false);
    setModelsError(null);
    setSaveSuccess(null);
    setSaveError(null);

    if (initialStateRef.current) {
      setModels(initialStateRef.current.models);
    } else {
      setModels([]);
    }
  }

  /* ---- Render helper: tooltip icon -------------------------------------- */

  /**
   * @param {string} tooltip - Tooltip text.
   * @returns {React.ReactElement}
   */
  function helpIcon(tooltip) {
    return <span className="settings-help-icon" data-tooltip={tooltip}>?</span>;
  }

  /* ---- Determine what to show in the model field ------------------------ */

  const hasLlmAndKey     = llmId && (apiKeyTouched ? apiKey : serverMaskedKey);
  const showModelSelect  = hasLlmAndKey && models.length > 0 && !modelsLoading;
  const showModelLoading = modelsLoading;

  /* ---- Render ----------------------------------------------------------- */

  return (
    <>
      <div className="settings-general">
        {/* Left panel: form fields */}
        <div className="settings-panel settings-panel--form">
          <div className="settings-form">

            {/* Username (read-only) */}
            <div className="settings-field">
              <div className="settings-label-row">
                <label className="settings-label">{t('settings.username.label')}</label>
                {helpIcon(t('settings.username.help'))}
              </div>
              <input
                className="settings-input"
                type="text"
                value={user?.username || ''}
                readOnly
              />
            </div>

            {/* Plan Tier (read-only) */}
            <div className="settings-field">
              <div className="settings-label-row">
                <label className="settings-label">{t('settings.planTier.label')}</label>
                {helpIcon(t('settings.planTier.help'))}
              </div>
              <input
                className="settings-input"
                type="text"
                value={user?.planTier || ''}
                readOnly
              />
            </div>

            {/* Scanner Engine LLM */}
            <div className="settings-field">
              <div className="settings-label-row">
                <label className="settings-label">{t('settings.llm.label')}</label>
                {helpIcon(t('settings.llm.help'))}
              </div>
              <select
                className={`settings-select${llmChanged ? ' settings-select--dirty' : ''}`}
                value={llmId}
                onChange={handleLlmChange}
                disabled={saving}
              >
                <option value="">{t('settings.llm.placeholder')}</option>
                {availableLlms.map((llm) => (
                  <option key={llm.id} value={llm.id}>{llm.name}</option>
                ))}
              </select>
            </div>

            {/* Scanner Engine LLM API Key */}
            <div className="settings-field">
              <div className="settings-label-row">
                <label className="settings-label">{t('settings.apiKey.label')}</label>
                {helpIcon(t('settings.apiKey.help'))}
              </div>
              <div className="settings-input-with-action">
                <input
                  className={`settings-input${keyChanged ? ' settings-input--dirty' : ''}`}
                  type="text"
                  placeholder={keyCleared || llmChanged ? '' : (serverMaskedKey || t('settings.apiKey.placeholder'))}
                  value={apiKey}
                  onChange={handleApiKeyChange}
                  onBlur={handleApiKeyBlur}
                  disabled={saving}
                />
                {(apiKey || serverMaskedKey) && !saving && (
                  <button
                    type="button"
                    className="settings-input-clear-btn"
                    onClick={handleApiKeyClear}
                    title={t('settings.apiKey.clear')}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Scanner Engine LLM Model */}
            <div className="settings-field">
              <div className="settings-label-row">
                <label className="settings-label">{t('settings.model.label')}</label>
                {helpIcon(t('settings.model.help'))}
              </div>

              {showModelLoading && (
                <span className="settings-model-hint">{t('settings.model.loading')}</span>
              )}

              {showModelSelect && (
                <select
                  className={`settings-select${modelChanged ? ' settings-select--dirty' : ''}`}
                  value={model}
                  onChange={handleModelChange}
                  disabled={saving}
                >
                  <option value="">{t('settings.model.placeholder')}</option>
                  {models.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              )}

              {modelsError && (
                <span className="settings-model-hint">{modelsError}</span>
              )}

              {!hasLlmAndKey && !modelsLoading && (
                <span className="settings-model-hint">{t('settings.model.needsLlmAndKey')}</span>
              )}
            </div>

            {/* Save / Revert */}
            {(saveSuccess || saveError) && (
              <div className={saveSuccess ? 'auth-success' : 'auth-error'}>
                {saveSuccess || saveError}
              </div>
            )}
            <div className="settings-button-row">
              <button
                type="button"
                className="settings-btn-revert"
                disabled={!isDirty || saving}
                onClick={handleRevert}
              >
                {t('settings.revert')}
              </button>
              <button
                type="button"
                className="settings-btn-save"
                disabled={!isDirty || saving}
                onClick={handleSave}
              >
                {t('settings.save')}
              </button>
            </div>
          </div>
        </div>

        {/* Right panel: account actions */}
        <div className="settings-panel settings-panel--actions">
          <button
            type="button"
            className="settings-btn-password"
            onClick={() => { setShowPasswordModal(true); setPasswordChangeSuccess(null); }}
          >
            {t('settings.changePassword')}
          </button>
          <button
            type="button"
            className="settings-btn-delete"
            onClick={() => setShowDeleteModal(true)}
          >
            {t('settings.deleteAccount')}
          </button>
          {passwordChangeSuccess && (
            <div className="auth-success">{passwordChangeSuccess}</div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showPasswordModal && (
        <ChangePasswordModal
          onClose={() => setShowPasswordModal(false)}
          onSuccess={() => {
            setShowPasswordModal(false);
            setPasswordChangeSuccess(t('settings.changePassword.success'));
          }}
        />
      )}
      {showDeleteModal && (
        <DeleteAccountModal onClose={() => setShowDeleteModal(false)} />
      )}
    </>
  );
}
